import React, { useState, useEffect } from 'react';
import { InvoiceProjectCustomer } from './invoiceType';
import InvoiceApi from './invoiceApi';
import { useNavigate } from 'react-router-dom';
import InvoicesTableView from './invoicesTableView';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { FilePlus } from 'lucide-react';

const InvoicePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Invoice', path: '/main/project/Invoice' },
  ];

  const [invoices, setInvoices] = useState<InvoiceProjectCustomer[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const data = await InvoiceApi.getInvoicesProjectsCustomers();
        setInvoices(data);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchInvoices();
  }, []);

  const handleEdit = (invoiceId: string) => {
    const selectedInvoice = invoices.find(invoice => invoice.invoice_id === invoiceId);

    if (selectedInvoice) {
      navigate('/main/project/invoice/update', { state: { invoice: selectedInvoice } });
    } else {
      setErrorMessage("Error: Invoice not found for update");
    }
  };

  const handleCustomerPayment = (invoiceId: string) => {
    const selectedInvoice = invoices.find(invoice => invoice.invoice_id === invoiceId);

    if (selectedInvoice) {
      //console.log('-----', selectedInvoice)
      navigate('/main/customerpayment/create', { state: { invoice: selectedInvoice } });
    } else {
      setErrorMessage("Error: Invoice not found for payment tracking");
    }
  };

  const handleDelete = async (invoiceId: string) => {
    const selectedInvoice = invoices.find(invoice => invoice.invoice_id === invoiceId);
    if (selectedInvoice) {
      navigate('/main/project/invoice/delete', { state: { invoice: selectedInvoice } });
    } else {
      setErrorMessage("Error: Invoice not found for deletion");
    }
  };

  const handleView = async (invoiceId: string) => {
    setErrorMessage('');

    const selectedInvoice = invoices.find(invoice => invoice.invoice_id === invoiceId);
    if (selectedInvoice) {
      try {
        const pdfBlob = await InvoiceApi.viewInvoicePdf(invoiceId);
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    } else {
      setErrorMessage("Error: Invoice not found");
    }
  };

  const handleSendToCustomer = (invoice: InvoiceProjectCustomer) => {
    //console.log('invoice==:', invoice)
    navigate('/main/project/invoice/tocustomer', { state: { invoice } });
  };

  const handleText = (invoice: InvoiceProjectCustomer, invoiceId: string) => {
    // Text functionality can be implemented here
    // window.location.href = `sms:${invoice.customer_phone}`;
  };

  const handleSendToQuickbooks = (invoice: InvoiceProjectCustomer) => {
    navigate('/main/project/invoice/toquickbooks', { state: { invoice } });
  };

  const handleCreateInvoice = () => {
    navigate('/main/project/invoice/create');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">

                Invoice List
              </h1>
              <Button size="medium" variant="default" onClick={handleCreateInvoice}>
                <FilePlus size={18} />
                Create Invoice
              </Button>
            </div>
            <p className="text-sm text-paragraph">
              Select radio button to edit & PDF view invoice, view payment, send invoice to customer or to Quickbooks. Click the down arrow to view itemized invoices.
            </p>
          </div>





          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Success Message */}
          {message && (
            <Alert variant="success" className="mb-6">
              {message}
            </Alert>
          )}

          {invoices.length > 0 ? (
            <div>
              <InvoicesTableView
                invoices={invoices}
                onEdit={handleEdit}
                onAddPayment={handleCustomerPayment}
                onDelete={handleDelete}
                onSendToCustomer={handleSendToCustomer}
                onSendToQuickbooks={handleSendToQuickbooks}
                onText={handleText}
                onView={handleView}
              />
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-paragraph mb-6">No invoice found, please click the Create Invoice button.</p>
              <Button onClick={handleCreateInvoice} size="medium" variant="default">
                <FilePlus className="size-5" />
                Create Invoice
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InvoicePage;